import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import axiosInstance from "../../utils/axiosConfig";
import { TailSpin } from "react-loader-spinner";
import { BiExport } from "react-icons/bi";
import { HiOutlineExclamationCircle } from "react-icons/hi";

import AdminTransactionsExportModal from "../../components/admin-export/AdminTransactionsExportModal";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { API_URL, PROD_LINK, STAGING_LINK } from "../../constant";
import { IS_STAGING } from "../../constant/api";
import { useAuth } from "../../context/auth-context";
import ConfirmResolveModal from "../orders/ConfirmResolveModal";
import ResolveModal from "../orders/ResolveModal";
import SuccessModal from "../orders/SuccessModal";
import { DataTable } from "../../components/data-table";
import type { AdminPayoutAssetDistributionItem } from "../../lib/api/adminPayoutSummaries";
import type { AdminPayoutTransaction } from "../../lib/api/adminTransactionsData";
import { useAdminPayoutSummariesQuery } from "../../lib/api/useAdminPayoutSummariesQuery";
import { useAdminPayoutTransactionsDataQuery } from "../../lib/api/useAdminPayoutTransactionsDataQuery";
import {
  banksLookupMap,
  useBanksLookupQuery,
} from "../../lib/api/useBanksLookupQuery";
import { formatAmount } from "../../utils/FormatPerson";
import AdminPayoutDetailModal from "./AdminPayoutDetailModal";
import {
  buildPayoutTableRows,
  createPayoutTableColumns,
  type PayoutTableRow,
} from "./payoutsTransactionsTable";

type PayoutDistributionBucketId = "crypto" | "giftcard" | "wallet";

const BUCKET_ORDER: PayoutDistributionBucketId[] = [
  "crypto",
  "giftcard",
  "wallet",
];

const PAYOUT_TABLE_LIMIT = 25;

const BUCKET_LABEL: Record<PayoutDistributionBucketId, string> = {
  crypto: "Crypto",
  giftcard: "Gift card",
  wallet: "Wallet & others",
};

const BUCKET_COLOR: Record<PayoutDistributionBucketId, string> = {
  crypto: "#279DF3",
  giftcard: "#F28A0F",
  wallet: "#4E606E",
};

/**
 * Payout distribution grouping (product rules):
 * - Crypto: USDT, SGIB, TRX, DOGE, BTC, ETH, SOL only.
 * - Wallet: NGN and DEEXPOINT / DEEX-POINT (normalized `deexpoint`).
 * - Gift card: everything else (e.g. g.play, APPLE, iTunes, …).
 */
const CRYPTO_TICKERS = new Set([
  "usdt",
  "sgib",
  "trx",
  "doge",
  "btc",
  "eth",
  "sol",
]);

function normalizeAssetTicker(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function isWalletBucket(tick: string, rawLower: string): boolean {
  if (tick === "ngn") return true;
  if (tick === "deexpoint") return true;
  if (rawLower.includes("deex") && rawLower.includes("point")) return true;
  return false;
}

function payoutAssetBucket(rawAsset: string): PayoutDistributionBucketId {
  const raw = rawAsset.trim();
  if (!raw) return "giftcard";
  const tick = normalizeAssetTicker(raw);
  const rawLower = raw.toLowerCase();

  if (CRYPTO_TICKERS.has(tick)) return "crypto";
  if (isWalletBucket(tick, rawLower)) return "wallet";
  return "giftcard";
}

type MergedAsset = {
  asset: string;
  payoutAmountUSD: number;
  payoutAmountNGN: number;
  count: number;
  assetAmount: number;
};

type CategorySegment = {
  id: PayoutDistributionBucketId;
  label: string;
  payoutAmountUSD: number;
  payoutAmountNGN: number;
  count: number;
  assetAmount: number;
  assets: MergedAsset[];
  pct: number;
  pctOfTotalUsd: number;
  color: string;
  showLabel: boolean;
};

function mergeAssetDistribution(
  items: AdminPayoutAssetDistributionItem[]
): MergedAsset[] {
  const map = new Map<string, MergedAsset>();
  for (const item of items) {
    const key = (item.asset ?? "—").trim().toUpperCase() || "—";
    const prev = map.get(key);
    const addUsd = Number(item.payoutAmountUSD) || 0;
    const addNgn = Number(item.payoutAmountNGN) || 0;
    const addCount = Number(item.count) || 0;
    const addAmt = Number(item.assetAmount) || 0;
    if (prev) {
      prev.payoutAmountUSD += addUsd;
      prev.payoutAmountNGN += addNgn;
      prev.count += addCount;
      prev.assetAmount += addAmt;
    } else {
      map.set(key, {
        asset: key,
        payoutAmountUSD: addUsd,
        payoutAmountNGN: addNgn,
        count: addCount,
        assetAmount: addAmt,
      });
    }
  }
  return Array.from(map.values());
}

function buildCategorySegments(
  merged: MergedAsset[],
  totalPayoutUsdFromApi: number
): CategorySegment[] {
  const byBucket = new Map<PayoutDistributionBucketId, MergedAsset[]>();
  for (const id of BUCKET_ORDER) byBucket.set(id, []);

  for (const row of merged) {
    const b = payoutAssetBucket(row.asset);
    byBucket.get(b)!.push(row);
  }

  const segments: CategorySegment[] = [];
  for (const id of BUCKET_ORDER) {
    const assets = (byBucket.get(id) ?? []).sort(
      (a, b) => b.payoutAmountUSD - a.payoutAmountUSD
    );
    const payoutAmountUSD = assets.reduce((s, a) => s + a.payoutAmountUSD, 0);
    const payoutAmountNGN = assets.reduce((s, a) => s + a.payoutAmountNGN, 0);
    const count = assets.reduce((s, a) => s + a.count, 0);
    const assetAmount = assets.reduce((s, a) => s + a.assetAmount, 0);
    if (payoutAmountUSD <= 0) continue;
    segments.push({
      id,
      label: BUCKET_LABEL[id],
      payoutAmountUSD,
      payoutAmountNGN,
      count,
      assetAmount,
      assets,
      pct: 0,
      pctOfTotalUsd: 0,
      color: BUCKET_COLOR[id],
      showLabel: true,
    });
  }

  const barTotal = segments.reduce((s, r) => s + r.payoutAmountUSD, 0);
  const totalUsd = totalPayoutUsdFromApi > 0 ? totalPayoutUsdFromApi : barTotal;

  for (const row of segments) {
    row.pct = barTotal > 0 ? (row.payoutAmountUSD / barTotal) * 100 : 0;
    row.pctOfTotalUsd =
      totalUsd > 0 ? (row.payoutAmountUSD / totalUsd) * 100 : 0;
    row.showLabel = row.pct >= 4;
  }

  return segments;
}

function formatStatusLabel(key: string): string {
  if (key === "COMEPLETED") return "Completed";
  return key
    .toLowerCase()
    .split(/_/g)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function statusAccent(key: string): { bar: string; glow: string } {
  const u = key.toUpperCase();
  if (u === "COMPLETED" || u === "SUCCESSFUL" || u === "COMEPLETED") {
    return { bar: "#03C668", glow: "rgba(3, 198, 104, 0.12)" };
  }
  if (u === "FAILED") {
    return { bar: "#DF4E4E", glow: "rgba(223, 78, 78, 0.12)" };
  }
  if (u === "PENDING") {
    return { bar: "#F28A0F", glow: "rgba(242, 138, 15, 0.12)" };
  }
  return { bar: "#0B75C2", glow: "rgba(11, 117, 194, 0.12)" };
}

function humanizeSourceKey(key: string): string {
  const k = key.trim();
  if (k === "" || k.toLowerCase() === "null") return "Unspecified";
  return k.replace(/-/g, " ");
}

const PayoutsOverview: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [autoPay, setAutoPay] = useState<string>("");
  const [autoPayBusy, setAutoPayBusy] = useState(false);

  const [resolveModal, setResolveModal] = useState(false);
  const [resolveConfirmModal, setResolveConfirmModal] = useState(false);
  const [resolveSuccessModal, setResolveSuccessModal] = useState(false);
  const [selectedPayouts, setSelectedPayouts] = useState<string[]>([]);
  const [selectedPayoutAmountById, setSelectedPayoutAmountById] = useState<
    Record<string, number>
  >({});
  const [resolveRefetchTick, setResolveRefetchTick] = useState(0);
  const [payoutTablePage, setPayoutTablePage] = useState(1);
  const [payoutTableSearch, setPayoutTableSearch] = useState("");
  const [payoutDetailOpen, setPayoutDetailOpen] = useState(false);
  const [payoutDetailTx, setPayoutDetailTx] =
    useState<AdminPayoutTransaction | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    axiosInstance
      .get(`${API_URL}api/settings`)
      .then((r: { data: { data: { autoPayStatus?: string } } }) => {
        setAutoPay(r.data.data.autoPayStatus ?? "");
      })
      .catch((e: { response?: { data?: { message?: string } } }) => {
        console.log(e.response?.data?.message);
      });
  }, [isAuthenticated]);

  const toggleAutoPay = () => {
    if (autoPayBusy || !isAuthenticated) return;
    const activating = autoPay !== "active";
    setAutoPayBusy(true);
    axiosInstance
      .patch(`${API_URL}api/settings/autopay-status`, {
        status: activating ? "active" : "inactive",
      })
      .then(() => {
        setAutoPay(activating ? "active" : "inactive");
      })
      .catch((e: { response?: { data?: { message?: string } } }) => {
        console.log(e.response?.data?.message);
      })
      .finally(() => setAutoPayBusy(false));
  };

  const dateRangeInvalid = Boolean(startDate && endDate) && startDate > endDate;

  const queryDates = useMemo(() => {
    if (startDate && endDate && !dateRangeInvalid) {
      return { startDate, endDate };
    }
    return {};
  }, [startDate, endDate, dateRangeInvalid]);

  const { data, isPending, isError, error, refetch, isFetching } =
    useAdminPayoutSummariesQuery({
      ...queryDates,
      enabled: !dateRangeInvalid,
    });

  useEffect(() => {
    if (resolveRefetchTick > 0) void refetch();
  }, [resolveRefetchTick, refetch]);

  useEffect(() => {
    setPayoutTablePage(1);
  }, [startDate, endDate, payoutTableSearch, dateRangeInvalid]);

  const { data: banksLookupData } = useBanksLookupQuery(!dateRangeInvalid);
  const banksByCode = useMemo(
    () => banksLookupMap(banksLookupData),
    [banksLookupData]
  );

  const {
    data: payoutTxPayload,
    isPending: payoutTxPending,
    isError: payoutTxError,
    error: payoutTxQueryError,
    refetch: refetchPayoutTx,
    isFetching: payoutTxFetching,
  } = useAdminPayoutTransactionsDataQuery({
    page: payoutTablePage,
    search: payoutTableSearch,
    limit: PAYOUT_TABLE_LIMIT,
    ...queryDates,
    enabled: !dateRangeInvalid,
  });

  const payoutTransactions = payoutTxPayload?.transactions ?? [];
  const payoutPagination = payoutTxPayload?.pagination;

  const payoutTableRows = useMemo(
    () => buildPayoutTableRows(payoutTransactions, banksByCode),
    [payoutTransactions, banksByCode]
  );

  const openPayoutDetail = useCallback((row: PayoutTableRow) => {
    setPayoutDetailTx(row.raw);
    setPayoutDetailOpen(true);
  }, []);

  const payoutColumns = useMemo(
    () => createPayoutTableColumns(openPayoutDetail),
    [openPayoutDetail]
  );

  const payoutTableTotalPages = Math.max(1, payoutPagination?.totalPages ?? 1);
  const payoutTableTotalCount = payoutPagination?.totalCount ?? 0;

  const openFailedPayoutResolveFlow = useCallback(() => {
    if (!isAuthenticated) return;
    setSelectedPayouts([]);
    setSelectedPayoutAmountById({});
    setResolveModal(true);
  }, [isAuthenticated]);

  const mergedAssets = useMemo(() => {
    if (!data?.assetDistribution?.length) return [];
    return mergeAssetDistribution(data.assetDistribution);
  }, [data?.assetDistribution]);

  const categorySegments = useMemo(() => {
    if (!data || mergedAssets.length === 0) return [];
    const totalUsd = Number(data.totalPayoutUSD) || 0;
    return buildCategorySegments(mergedAssets, totalUsd);
  }, [mergedAssets, data]);

  const [selectedBucketId, setSelectedBucketId] =
    useState<PayoutDistributionBucketId | null>(null);

  useEffect(() => {
    if (categorySegments.length === 0) {
      setSelectedBucketId(null);
      return;
    }
    setSelectedBucketId((prev) =>
      prev && categorySegments.some((s) => s.id === prev)
        ? prev
        : categorySegments[0].id
    );
  }, [categorySegments]);

  const selectedSegment = useMemo(() => {
    if (categorySegments.length === 0) return null;
    return (
      categorySegments.find((s) => s.id === selectedBucketId) ??
      categorySegments[0]
    );
  }, [categorySegments, selectedBucketId]);

  const statusEntries = useMemo(() => {
    if (!data?.statusBreakdown) return [];
    return Object.entries(data.statusBreakdown).sort((a, b) => b[1] - a[1]);
  }, [data?.statusBreakdown]);

  const sourceEntries = useMemo(() => {
    if (!data?.sourceBreakdown) return [];
    return Object.entries(data.sourceBreakdown).sort(
      (a, b) => b[1].count - a[1].count
    );
  }, [data?.sourceBreakdown]);

  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      <div className="w-full h-[31px] shrink-0 bg-[#1B1B1B] flex justify-center items-center">
        {IS_STAGING ? (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in Staging:{" "}
            <span>
              <a href={PROD_LINK} className="text-[#03C668]">
                Switch to Prod
              </a>
            </span>
          </h4>
        ) : (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in PROD:{" "}
            <span>
              <a href={STAGING_LINK} className="text-[#DF4E4E]">
                Switch to Staging
              </a>
            </span>
          </h4>
        )}
      </div>
      <div className="flex flex-1 min-h-0 w-full flex-col lgss:flex-row overflow-hidden">
        <Sidebar />
        <div className="w-full lgss:w-10/12 flex-1 min-h-0 min-w-0 flex flex-col overflow-y-auto bg-[#13181B]">
          <Navbar />
          <div className="flex-1 px-5 pt-6 pb-24 mds:px-8 lgss:px-10 font-manrope bg-[#13181B] max-w-[1600px] mx-auto w-full">
            <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-8">
              <div>
                <h1 className="font-sora font-semibold text-white text-xl tracking-tight">
                  Payouts
                </h1>
                <p className="text-[13px] text-[#869AA9] mt-1 max-w-xl leading-relaxed">
                  Summary for the selected period. Leave dates empty for
                  all-time totals. The payout transactions table uses the same
                  date range as the summary cards.
                </p>
              </div>

              <div
                className="rounded-xl border border-[#2C363E] bg-[#161E25]/80 p-4 shadow-[0_4px_24px_rgba(0,0,0,0.25)] w-full lg:w-auto lg:min-w-[320px]"
                role="group"
                aria-label="Date range filter"
              >
                <p className="text-[11px] font-semibold text-[#869AA9] uppercase tracking-wider mb-3">
                  Date range
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                  <div className="flex-1 min-w-0">
                    <label
                      htmlFor="payouts-summary-start"
                      className="block text-[11px] text-[#5C6B76] mb-1"
                    >
                      From
                    </label>
                    <input
                      id="payouts-summary-start"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full h-10 rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40 transition shadow-inner"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label
                      htmlFor="payouts-summary-end"
                      className="block text-[11px] text-[#5C6B76] mb-1"
                    >
                      To
                    </label>
                    <input
                      id="payouts-summary-end"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full h-10 rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40 transition shadow-inner"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                    }}
                    className="h-10 shrink-0 px-4 rounded-lg border border-[#2C363E] text-[12px] font-semibold text-[#AEBCC6] hover:text-white hover:bg-[#273037] hover:border-[#3A4852] transition active:scale-[0.98]"
                  >
                    Reset
                  </button>
                </div>
                {(startDate || endDate) && !(startDate && endDate) && (
                  <p className="mt-2 text-[11px] text-[#5C6B76]">
                    Select both dates to filter; otherwise all-time data is
                    shown.
                  </p>
                )}
              </div>
            </header>

            {dateRangeInvalid && (
              <div
                className="rounded-xl border border-amber-500/35 bg-amber-500/[0.07] px-5 py-8 text-center mb-6"
                role="alert"
              >
                <p className="text-[14px] font-semibold text-amber-200/95">
                  Invalid date range
                </p>
                <p className="text-[13px] text-[#AEBCC6] mt-2 max-w-md mx-auto leading-relaxed">
                  Choose an end date on or after the start date, or reset the
                  filter.
                </p>
              </div>
            )}

            {!dateRangeInvalid && isPending && (
              <div
                className="flex flex-col items-center justify-center py-24 gap-4 rounded-xl border border-[#2C363E] bg-[#161E25]/40"
                aria-busy="true"
              >
                <TailSpin color="#0B75C2" height={40} width={40} />
                <p className="text-[13px] text-[#869AA9]">Loading summaries…</p>
              </div>
            )}

            {!dateRangeInvalid && isError && (
              <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-4 text-[13px] text-red-100/95 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <span>
                  {error?.response?.data &&
                  typeof error.response.data === "object" &&
                  "message" in error.response.data
                    ? String(
                        (error.response.data as { message?: string }).message
                      )
                    : "Could not load payout summaries."}
                </span>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="shrink-0 rounded-lg bg-red-500/20 px-4 py-2 text-[12px] font-semibold text-red-200 border border-red-500/40 hover:bg-red-500/30 transition"
                >
                  Retry
                </button>
              </div>
            )}

            {!dateRangeInvalid && !isPending && !isError && data && (
              <>
                {isFetching && !isPending && (
                  <div className="mb-4 flex items-center gap-2 text-[12px] text-[#869AA9]">
                    <span
                      className="inline-block h-3 w-3 rounded-full border-2 border-[#869AA9] border-t-transparent animate-spin"
                      aria-hidden
                    />
                    Refreshing…
                  </div>
                )}

                <section
                  className="rounded-2xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#181D22] p-5 sm:p-7 mb-6 shadow-[0_8px_32px_rgba(0,0,0,0.28)]"
                  aria-labelledby="payout-total-heading"
                >
                  <p
                    id="payout-total-heading"
                    className="text-[11px] font-bold text-[#869AA9] uppercase tracking-widest"
                  >
                    Total payout
                  </p>
                  <p className="font-sora font-bold text-white text-2xl sm:text-4xl mt-2 tabular-nums">
                    ₦{formatAmount(data.totalPayoutNGN)}
                  </p>
                  <p className="font-sora text-[13px] text-[#869AA9] mt-2">
                    {" "}
                    ${formatAmount(data.totalPayoutUSD)}
                  </p>

                  <div className="border-t border-[#2C363E]/80 pt-6 mt-6">
                    <h3 className="text-[12px] font-semibold text-[#E7EBEE] mb-1">
                      Distribution
                    </h3>
                    <p className="text-[11px] text-[#5C6B76] mb-3">
                      Crypto: USDT, SGIB, TRX, DOGE, BTC, ETH, SOL. Wallet: NGN
                      and DEEXPOINT. All other assets (e.g. g.play) count as
                      gift card. Select a segment for the full asset list.
                    </p>
                    {categorySegments.length === 0 ? (
                      <p className="text-[13px] text-[#869AA9] py-6 text-center rounded-lg bg-[#141920]/80 border border-dashed border-[#2C363E]">
                        No asset distribution for this range.
                      </p>
                    ) : (
                      <>
                        <div
                          className="flex w-full gap-1 p-1 rounded-xl bg-[#0D1114] min-h-[44px] shadow-inner"
                          role="group"
                          aria-label="Payout mix by asset group"
                        >
                          {categorySegments.map((s) => {
                            const isSel = selectedBucketId === s.id;
                            return (
                              <button
                                key={s.id}
                                type="button"
                                aria-pressed={isSel}
                                onClick={() => setSelectedBucketId(s.id)}
                                className={`group relative flex items-center justify-center min-h-[44px] min-w-0 first:rounded-l-lg last:rounded-r-lg rounded-md py-2 px-0.5 transition-all hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1114] ${
                                  isSel
                                    ? "ring-2 ring-white/90 ring-inset z-[1] scale-[1.02]"
                                    : ""
                                }`}
                                style={{
                                  flexGrow: Math.max(s.pct, 0.35),
                                  flexBasis: 0,
                                  backgroundColor: s.color,
                                }}
                              >
                                {s.showLabel ? (
                                  <span className="pointer-events-none truncate px-1 text-[11px] font-semibold text-white drop-shadow-md sm:text-[12px]">
                                    {s.pct < 0.1
                                      ? `${s.pct.toFixed(2)}%`
                                      : `${s.pct.toFixed(1)}%`}
                                  </span>
                                ) : (
                                  <span className="sr-only">
                                    {s.label}: {s.pct.toFixed(2)}%
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-3 flex w-full gap-1 px-1">
                          {categorySegments.map((s) => {
                            const isSel = selectedBucketId === s.id;
                            return (
                              <div
                                key={`leg-${s.id}`}
                                className="min-w-0 flex justify-center"
                                style={{
                                  flexGrow: Math.max(s.pct, 0.35),
                                  flexBasis: 0,
                                }}
                              >
                                <button
                                  type="button"
                                  aria-pressed={isSel}
                                  onClick={() => setSelectedBucketId(s.id)}
                                  className={`max-w-full truncate rounded-md px-1.5 py-1 text-[10px] sm:text-[11px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary_button ${
                                    isSel
                                      ? "text-white bg-[#2C363E]"
                                      : "text-[#869AA9] hover:text-[#C6D0D7]"
                                  }`}
                                >
                                  {s.label}
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        {selectedSegment && (
                          <div
                            className="mt-6 rounded-xl border border-[#2C363E] bg-[#141920]/95 p-4 sm:p-5"
                            style={{
                              borderLeftWidth: 4,
                              borderLeftColor: selectedSegment.color,
                            }}
                          >
                            <h4 className="font-sora text-lg font-bold text-white">
                              {selectedSegment.label}
                            </h4>
                            <p className="text-[12px] text-[#5C6B76] mt-1">
                              {selectedSegment.assets.length} asset
                              {selectedSegment.assets.length === 1
                                ? ""
                                : "s"}{" "}
                              in this group
                            </p>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                              <div className="rounded-lg bg-[#1C2227]/80 border border-[#2C363E]/60 px-3 py-3">
                                <dt className="text-[10px] font-semibold text-[#5C6B76] uppercase tracking-wide">
                                  USD (group)
                                </dt>
                                <dd className="text-[15px] font-semibold text-[#BEE1FB] tabular-nums mt-1">
                                  $
                                  {formatAmount(
                                    selectedSegment.payoutAmountUSD
                                  )}
                                </dd>
                              </div>
                              <div className="rounded-lg bg-[#1C2227]/80 border border-[#2C363E]/60 px-3 py-3">
                                <dt className="text-[10px] font-semibold text-[#5C6B76] uppercase tracking-wide">
                                  NGN (group)
                                </dt>
                                <dd className="text-[15px] font-semibold text-[#E7EBEE] tabular-nums mt-1">
                                  ₦
                                  {formatAmount(
                                    selectedSegment.payoutAmountNGN
                                  )}
                                </dd>
                              </div>
                              <div className="rounded-lg bg-[#1C2227]/80 border border-[#2C363E]/60 px-3 py-3">
                                <dt className="text-[10px] font-semibold text-[#5C6B76] uppercase tracking-wide">
                                  Count (group)
                                </dt>
                                <dd className="text-[15px] font-semibold text-white tabular-nums mt-1">
                                  {selectedSegment.count.toLocaleString()}
                                </dd>
                              </div>
                            </dl>

                            <div className="mt-5 overflow-x-auto rounded-lg border border-[#2C363E]">
                              <table className="w-full min-w-[520px] text-left text-[12px]">
                                <thead className="bg-[#1C2227] text-[#869AA9]">
                                  <tr>
                                    <th className="px-3 py-2.5 font-semibold">
                                      Asset
                                    </th>
                                    <th className="px-3 py-2.5 font-semibold text-right tabular-nums">
                                      USD
                                    </th>
                                    <th className="px-3 py-2.5 font-semibold text-right tabular-nums">
                                      NGN
                                    </th>
                                    <th className="px-3 py-2.5 font-semibold text-right tabular-nums">
                                      Count
                                    </th>
                                    <th className="px-3 py-2.5 font-semibold text-right tabular-nums">
                                      Native amt.
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="text-[#E7EBEE]">
                                  {selectedSegment.assets.map((a) => (
                                    <tr
                                      key={a.asset}
                                      className="border-t border-[#2C363E] bg-[#0D1114]/40"
                                    >
                                      <td className="px-3 py-2.5 font-medium text-white">
                                        {a.asset}
                                      </td>
                                      <td className="px-3 py-2.5 text-right tabular-nums">
                                        ${formatAmount(a.payoutAmountUSD)}
                                      </td>
                                      <td className="px-3 py-2.5 text-right tabular-nums">
                                        ₦{formatAmount(a.payoutAmountNGN)}
                                      </td>
                                      <td className="px-3 py-2.5 text-right tabular-nums">
                                        {a.count.toLocaleString()}
                                      </td>
                                      <td className="px-3 py-2.5 text-right tabular-nums break-all">
                                        {formatAmount(a.assetAmount)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </section>

                {data.totalFailed > 0 && (
                  <div
                    className="rounded-xl border border-red-500/40 bg-red-950/50 px-4 py-4 sm:px-5 sm:py-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                    role="alert"
                  >
                    <div className="flex gap-3 min-w-0">
                      <HiOutlineExclamationCircle
                        className="shrink-0 text-red-400 text-2xl"
                        aria-hidden
                      />
                      <div>
                        <p className="text-[14px] font-bold text-white">
                          Some payouts failed
                        </p>
                        <p className="text-[13px] text-red-200/80 mt-1">
                          You have {data.totalFailed.toLocaleString()} payout
                          {data.totalFailed === 1 ? "" : "s"} with failed status
                          in this range.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={openFailedPayoutResolveFlow}
                      disabled={!isAuthenticated}
                      className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-[12px] font-semibold text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      Review
                    </button>
                  </div>
                )}

                <section
                  className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6"
                  aria-label="Payout counts"
                >
                  {[
                    {
                      label: "Total payouts",
                      value: data.totalCount.toLocaleString(),
                    },
                    {
                      label: "Successful",
                      value: data.totalSuccess.toLocaleString(),
                    },
                    {
                      label: "Pending",
                      value: data.totalPending.toLocaleString(),
                    },
                  ].map((kpi) => (
                    <div
                      key={kpi.label}
                      className="rounded-xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#1A1F24] px-4 py-4 shadow-[0_2px_12px_rgba(0,0,0,0.2)]"
                    >
                      <p className="text-[11px] font-semibold text-[#869AA9] uppercase tracking-wider">
                        {kpi.label}
                      </p>
                      <p className="font-sora text-xl sm:text-2xl font-bold text-white mt-2 tabular-nums">
                        {kpi.value}
                      </p>
                    </div>
                  ))}
                </section>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
                  <section aria-labelledby="payout-status-heading">
                    <div className="flex items-baseline justify-between gap-2 mb-4">
                      <h2
                        id="payout-status-heading"
                        className="text-[12px] font-bold text-[#869AA9] uppercase tracking-widest"
                      >
                        Status
                      </h2>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {statusEntries.map(([key, count]) => {
                        const { bar, glow } = statusAccent(key);
                        return (
                          <div
                            key={key}
                            className="rounded-xl border border-[#2C363E] bg-[#1C2227] px-4 py-3.5"
                            style={{
                              boxShadow: `inset 0 0 0 1px ${glow}`,
                              borderLeftWidth: 3,
                              borderLeftColor: bar,
                            }}
                          >
                            <p className="text-[11px] text-[#869AA9] mb-1.5">
                              {formatStatusLabel(key)}
                            </p>
                            <p className="font-sora text-xl font-bold text-white tabular-nums">
                              {count.toLocaleString()}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  {sourceEntries.length > 0 && (
                    <section aria-labelledby="payout-source-heading">
                      <h2
                        id="payout-source-heading"
                        className="text-[12px] font-bold text-[#869AA9] uppercase tracking-widest mb-4"
                      >
                        By source
                      </h2>
                      <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                        {sourceEntries.map(([key, row]) => (
                          <div
                            key={key}
                            className="rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 py-2.5 flex flex-wrap items-baseline justify-between gap-2"
                          >
                            <span className="text-[13px] font-semibold text-[#E7EBEE]">
                              {humanizeSourceKey(key)}
                            </span>
                            <span className="text-[12px] text-[#AEBCC6] tabular-nums">
                              {row.count.toLocaleString()} · $
                              {formatAmount(row.payoutAmountUSD)} · ₦
                              {formatAmount(row.payoutAmountNGN)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </div>

                <div className="w-full flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mt-4 mb-3">
                  <h4 className="text-white font-sora font-semibold text-[12px] shrink-0">
                    Activity
                  </h4>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div className="flex items-center gap-2">
                      {autoPayBusy ? (
                        <TailSpin color="#0B75C2" height={20} width={20} />
                      ) : (
                        <button
                          type="button"
                          onClick={toggleAutoPay}
                          aria-pressed={autoPay === "active"}
                          aria-label="Toggle auto pay"
                          className={`flex w-8 h-4 shrink-0 rounded-full transition-all duration-500 cursor-pointer p-0 border-0 ${
                            autoPay === "active"
                              ? "bg-secondary_button"
                              : "bg-gray-600"
                          }`}
                        >
                          <span
                            className={`h-4 w-4 rounded-full transition-all duration-500 bg-gray-100 pointer-events-none ${
                              autoPay === "active" ? "ml-4" : ""
                            }`}
                          />
                        </button>
                      )}
                      <span className="uppercase font-sora font-semibold text-[#869AA9] text-[10px]">
                        Auto Pay:{" "}
                        <span
                          className={
                            autoPay === "active" ? "text-[#359943]" : undefined
                          }
                        >
                          {autoPay === "active" ? " ON" : " OFF"}
                        </span>
                      </span>
                    </div>
                    <span
                      className="text-[#3A4852] hidden sm:inline"
                      aria-hidden
                    >
                      |
                    </span>
                    <button
                      type="button"
                      onClick={() => setExportOpen(true)}
                      className="flex items-center justify-center gap-2 cursor-pointer opacity-90 hover:opacity-100"
                      aria-label="Export summaries and payouts"
                    >
                      <BiExport className="text-white text-lg" aria-hidden />
                      <span className="uppercase font-sora font-semibold text-[#869AA9] text-[10px] mt-0.5">
                        Export
                      </span>
                    </button>
                  </div>
                </div>

                <div id="payouts-transactions" className="mb-2">
                  <div className="mb-4">
                    <input
                      type="search"
                      value={payoutTableSearch}
                      onChange={(e) => setPayoutTableSearch(e.target.value)}
                      placeholder="Search transactions"
                      className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 text-[13px] text-[#E7EBEE] outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                    />
                  </div>
                  {payoutTxFetching && !payoutTxPending && (
                    <p className="text-[12px] text-[#869AA9] mb-2">
                      Updating list…
                    </p>
                  )}

                  {payoutTxError && (
                    <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <span>
                        {axios.isAxiosError(payoutTxQueryError) &&
                        payoutTxQueryError.response?.data &&
                        typeof payoutTxQueryError.response.data === "object" &&
                        "message" in payoutTxQueryError.response.data
                          ? String(
                              (
                                payoutTxQueryError.response.data as {
                                  message?: string;
                                }
                              ).message
                            )
                          : "Could not load payout transactions."}
                      </span>
                      <button
                        type="button"
                        onClick={() => void refetchPayoutTx()}
                        className="shrink-0 rounded-lg bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200 border border-red-500/40"
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  {payoutTxPending && !payoutTxError && (
                    <div className="flex justify-center py-16 rounded-xl border border-[#2C363E] bg-[#161E25]/40">
                      <TailSpin color="#0B75C2" height={36} width={36} />
                    </div>
                  )}

                  {!payoutTxPending && !payoutTxError && (
                    <DataTable<PayoutTableRow>
                      columns={payoutColumns}
                      data={payoutTableRows}
                      currentPage={payoutTablePage}
                      setCurrentPage={setPayoutTablePage}
                      totalPage={payoutTableTotalPages}
                      count={payoutTableTotalCount}
                      limit={PAYOUT_TABLE_LIMIT}
                      emptyContent="No payout transactions for this filter."
                      className="rounded-xl border border-[#2C363E] overflow-hidden"
                      fixedBodyHeight="min(560px, 52vh)"
                    />
                  )}

                  <AdminPayoutDetailModal
                    open={payoutDetailOpen}
                    onClose={() => {
                      setPayoutDetailOpen(false);
                      setPayoutDetailTx(null);
                    }}
                    transaction={payoutDetailTx}
                    banksByCode={banksByCode}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {resolveModal && (
        <ResolveModal
          setResolveModal={setResolveModal}
          setResolveConfirmModal={setResolveConfirmModal}
          selectedPayouts={selectedPayouts}
          setSelectedPayouts={setSelectedPayouts}
          selectedPayoutAmountById={selectedPayoutAmountById}
          setSelectedPayoutAmountById={setSelectedPayoutAmountById}
          setRefetchPayoutCount={setResolveRefetchTick}
        />
      )}
      {resolveConfirmModal && (
        <ConfirmResolveModal
          setResolveConfirmModal={setResolveConfirmModal}
          setResolveSuccessModal={setResolveSuccessModal}
          selectedPayouts={selectedPayouts}
          onResolved={() => {
            setSelectedPayouts([]);
            setSelectedPayoutAmountById({});
          }}
        />
      )}
      {resolveSuccessModal && (
        <SuccessModal
          setResolveSuccessModal={setResolveSuccessModal}
          setRefetchCounting={setResolveRefetchTick}
        />
      )}
      <AdminTransactionsExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Payouts"
        exportContext={{ type: "payouts" }}
      />
    </div>
  );
};

export default PayoutsOverview;
